import { useEffect, useState } from "react";
import { Users, Crown, DollarSign, Film, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Stat } from "./AdminUI";
import { RadioListenerStats } from "./RadioListenerStats";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    endpoints.adminDashboard().then((r) => setStats(r.data)).catch(() => {});
    endpoints.adminRevenue().then((r) => setRevenue(r.data)).catch(() => {});
    endpoints.adminPayments().then((r) => setPayments(r.data.slice(0, 8))).catch(() => {});
  }, []);

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>;
  const rwf = stats.revenue?.allTime?.RWF || 0;

  return (
    <div>
      <PageHeader title="Dashboard" desc="Overview of B&B KIGALI performance." />
      <RadioListenerStats />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
        <Stat label="Total revenue" value={`${Number(rwf).toLocaleString()} RWF`} icon={DollarSign} color="#22C55E" />
        <Stat label="Active members" value={stats.subscriptions?.active ?? 0} icon={Crown} color="#2563EB" />
        <Stat label="Total users" value={stats.users?.total ?? 0} icon={Users} color="#2563EB" />
        <Stat label="Shows" value={stats.content?.shows ?? 0} icon={Film} color="#E11D2A" />
      </div>

      <Card className="mt-6 p-5">
        <h3 className="text-white font-bold mb-4">Revenue (RWF)</h3>
        {revenue.length === 0 ? <div className="text-slate-500 text-sm py-16 text-center">No revenue yet.</div> : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={revenue.filter((r) => r.currency === "RWF")}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E11D2A" stopOpacity={0.5} /><stop offset="100%" stopColor="#E11D2A" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#273042" />
              <XAxis dataKey="period" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#121620", border: "1px solid #273042", borderRadius: 8, color: "#fff" }} />
              <Area type="monotone" dataKey="amount" stroke="#E11D2A" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="mt-6 overflow-hidden">
        <div className="p-4 border-b border-[#273042] text-white font-bold">Recent payments</div>
        {payments.length === 0 ? <div className="text-slate-500 text-sm py-10 text-center">No payments yet.</div> : (
          <div className="divide-y divide-[#273042]">
            {payments.map((p) => (
              <div key={p.id} className="p-4 flex items-center justify-between text-sm">
                <div><div className="text-white">{p.userPhone}</div><div className="text-xs text-slate-400">{p.planLabel} · {p.method}</div></div>
                <div className="text-right"><div className="text-white font-semibold">{Number(p.amount).toLocaleString()} {p.currency}</div><div className={`text-xs uppercase font-bold ${p.status === "paid" ? "text-[#22C55E]" : "text-slate-400"}`}>{p.status}</div></div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
